import getMaxZoomScale from "@components/Atoms/Image/modalImage/getMaxZoomScale";
import getSvgAspectRatio, { getSvgViewBoxSize } from "@components/Atoms/Image/modalImage/getSvgAspectRatio";
import { cn } from "@core-ui/utils/cn";
import isSafari from "@core-ui/utils/isSafari";
import type { ImageObject } from "@ext/markdown/elements/image/edit/model/imageEditorTypes";
import ObjectRenderer from "@ext/markdown/elements/image/render/components/ObjectRenderer";
import { type CSSProperties, memo, useLayoutEffect, useMemo, useRef } from "react";
import { TransformComponent } from "react-zoom-pan-pinch";

interface ImageProps {
	id: string;
	objects: ImageObject[];
	src?: string;
	svg?: string;
	className?: string;
	modalStyle?: CSSProperties;
	html?: string | TrustedHTML;
	onMaxScaleChange: (maxScale: number) => void;
}

export type Rect = {
	left: number;
	top: number;
	scale: number;
};

const MediaRenderer = (props: ImageProps) => {
	const { id, className, src, svg, objects = [], modalStyle, onMaxScaleChange } = props;
	const imgRef = useRef<HTMLImageElement>();
	const containerRef = useRef<HTMLDivElement>();
	const aspectRatio = useMemo(() => getSvgAspectRatio(svg), [svg]);

	useLayoutEffect(() => {
		if (!svg || !imgRef.current) return;
		onMaxScaleChange(getMaxZoomScale(imgRef.current, getSvgViewBoxSize(svg)?.width ?? 0));
	}, [svg, onMaxScaleChange]);

	return (
		<div className={cn("w-full h-full content-center justify-items-center", className)}>
			<TransformComponent
				contentClass={isSafari() ? "will-change-transform" : undefined}
				wrapperClass="transform-wrapper !w-full !h-full !absolute !top-0 !left-0"
			>
				<div
					className={cn("image-container", svg && "image-container--svg h-[80dvh] max-w-[80dvw]")}
					ref={containerRef}
					style={aspectRatio ? { aspectRatio } : undefined}
				>
					{svg ? (
						<div
							className={cn(
								"flex h-full w-full items-center justify-center [&>svg]:!h-full",
								// Пропорцию держит коробка, если она у неё есть; иначе — сам рисунок.
								aspectRatio ? "[&>svg]:!w-full" : "[&>svg]:!w-auto [&>svg]:!max-w-none",
							)}
							// biome-ignore lint/style/useNamingConvention: expected
							dangerouslySetInnerHTML={{ __html: svg }}
							draggable={false}
							id={id}
							ref={imgRef}
							style={modalStyle}
						/>
					) : (
						<img
							alt=""
							// react-zoom-pan-pinch CSS disables pointer events on img, which hides the native "Copy image" menu.
							className="!pointer-events-auto"
							draggable="false"
							id={id}
							key={id}
							onLoad={(e) =>
								onMaxScaleChange(getMaxZoomScale(e.currentTarget, e.currentTarget.naturalWidth))
							}
							ref={imgRef}
							src={src}
							style={modalStyle}
						/>
					)}

					<div className="object-container">
						<ObjectRenderer editable={false} imageRef={imgRef} objects={objects} parentRef={containerRef} />
					</div>
				</div>
			</TransformComponent>
		</div>
	);
};

export default memo(MediaRenderer);
